/* « E-mails aux clients » — the hub's tab (migration pack messages, v3.3 WF-028, the automatic half).
   What the merchant can see today: the sender her mail leaves from (the platform's, in her name — the honest status
   Modeles.tsx always printed), and the automatic messages of the journey the app already carries ('@/emails', read-only),
   each with a « Prévisualiser » door to its existing preview page. The reference's other half — « Messages rédigés par
   le salon » (a compose dialog, drafts, a simulation history, a merchant-owned sender) — is a later phase (WF-029,
   WF-107…113, WF-115): one honest line each, no invented state, no fake page.
   Guardrail: the classes stay separated at delivery — the review invitation never mentions the gift (rule 1, '@/emails'). */
import { useNavigate } from 'react-router-dom'
import { Button, Card, ErrorState, List, ListRow, Note, Pill } from '@/ui'
import { TEMPLATES } from '@/emails/templates'
import { useTemplates } from './api'
import { Chargement } from './parts'

/** The automatic messages a merchant can open — the same doors as the library (Modeles.tsx ECRAN); the 14-day
    expiration reminder exists as a template with no screen yet (WF-115, rule 14 pending): a line, no door. « Revenez
    jouer » is the other way round (messages vet rider 1): its screen exists (EM-12, routes/messages/RevenezJouer) and its
    template does not yet — the row is described here, in the class's own words (Classes.tsx), until '@/emails' carries it. */
const PORTES: Record<string, string> = {
  cadeau: '/messages/apercu/cadeau',
  avis: '/messages/apercu/avis',
  'rappel-retour': '/messages/apercu/rappel',
  'revenez-jouer': '/messages/revenez-jouer',
}
const AUTOMATIQUES = ['cadeau', 'rappel-expiration', 'avis', 'rappel-retour', 'revenez-jouer']
const SANS_MODELE = [{ id: 'revenez-jouer', name: '« Revenez jouer »', classeLabel: 'Retour — envoyé le jour où la fenêtre du client se rouvre, une fois, jamais deux' }]

export default function EmailsClients() {
  const { data, isPending, isError, refetch } = useTemplates()
  const nav = useNavigate()

  if (isPending) return <Chargement />
  if (isError) return (
    <ErrorState what="Impossible de charger vos e-mails."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  const modeles = AUTOMATIQUES.map(id => TEMPLATES.find(t => t.id === id) ?? SANS_MODELE.find(t => t.id === id)).filter((t): t is NonNullable<typeof t> => !!t)
  const etat = (id: string) => data.rows.find(r => r.id === id)

  return (
    <>
      <Card data-testid="emails-expediteur">
        <h2 className="t-page" style={{ margin: '0 0 6px', fontSize: 17 }}>Expéditeur</h2>
        <p className="t-body" style={{ margin: 0 }}>{data.sender} · nom d’affichage&nbsp;: votre établissement</p>
        <p className="f-help" style={{ margin: '6px 0 0' }}>
          Vos e-mails partent de l’expéditeur de la plateforme, à votre nom. Un expéditeur à votre propre adresse, avec sa
          vérification, arrive dans une phase ultérieure&nbsp;: rien à configurer ici pour l’instant.
        </p>
        <div className="actions" style={{ marginTop: 10 }}>
          <Button size="sm" variant="quiet" onClick={() => nav('/parametres/integrations')}>Voir dans Intégrations</Button>
        </div>
      </Card>

      <Card data-testid="emails-automatiques">
        <h2 className="t-page" style={{ margin: '0 0 10px', fontSize: 17 }}>Messages automatiques du parcours</h2>
        <List>
          {modeles.map(t => {
            const r = etat(t.id)
            return (
              <ListRow key={t.id} title={t.name}
                meta={<>{t.classeLabel}{r && <> · {r.langue} · {r.version} <Pill tone={r.status === 'publiee' ? 'ok' : 'warn'} mark={r.status === 'brouillon' ? '✎' : undefined}>{r.statusLabel}</Pill></>}
                  {!PORTES[t.id] && <><br />Aperçu dans une phase ultérieure — la règle du rappel à 14 jours reste à confirmer.</>}</>}
                end={PORTES[t.id] && <Button size="sm" variant="quiet" onClick={() => nav(PORTES[t.id])}>Prévisualiser</Button>} />
            )
          })}
        </List>
        <Note tone="candour">
          Les classes restent séparées à l’envoi&nbsp;: le message du cadeau est transactionnel, l’invitation à donner un avis
          ne mentionne jamais le cadeau, et un rappel décrit un avantage déjà acquis ou ne part pas. Aucun avis n’est demandé
          dans un rappel.
        </Note>
        <div className="actions" style={{ marginTop: 10 }}>
          <Button size="sm" variant="quiet" onClick={() => nav('/messages/modeles')}>Bibliothèque de modèles</Button>
          <Button size="sm" variant="quiet" onClick={() => nav('/messages/journal')}>Journal d’envoi</Button>
        </div>
      </Card>

      <Card data-testid="emails-rediges">
        <h2 className="t-page" style={{ margin: '0 0 6px', fontSize: 17 }}>Messages rédigés par le salon</h2>
        <p className="f-help" style={{ margin: 0 }}>
          Écrire un e-mail à une cliente, enregistrer un brouillon et retrouver l’historique des simulations&nbsp;: phase
          ultérieure. Rien n’est préparé ni envoyé depuis cette page.
        </p>
      </Card>
    </>
  )
}
